import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingScreen from '../common/LoadingScreen';
import MainLayout from '../layout/MainLayout';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
}

function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading, needsQuestionnaire } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect ALL users to questionnaire if they haven't completed it
  // Skip if user still needs to change password first (password change takes priority)
  const isSuperSuperAdmin = user && user.role === 'super-super-admin';
  if (needsQuestionnaire && location.pathname !== '/questionnaire' && !isSuperSuperAdmin && !user?.mustChangePassword) {
    return <Navigate to="/questionnaire" replace />;
  }

  // Super-super-admin can ONLY access /admin/super and /profile — redirect everything else
  if (user?.role === 'super-super-admin' && !location.pathname.startsWith('/admin/super') && location.pathname !== '/profile' && location.pathname !== '/settings') {
    return <Navigate to="/admin/super" replace />;
  }

  // Check role-based access
  if (requiredRoles && requiredRoles.length > 0) {
    if (!user || !requiredRoles.includes(user.role)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Wrap protected routes with MainLayout (except questionnaire page)
  if (location.pathname === '/questionnaire') {
    return <>{children}</>;
  }

  return (
    <MainLayout>
      {children}
    </MainLayout>
  );
}

export default ProtectedRoute;
